// Privacy settings and filters for Sentry Session Replay (used in plugins/Sentry.js).
//
// Replay masks all text and inputs and blocks all media. These settings and filters
// cover what that does not: attribute values, link targets, document HTML, console
// output, user details, and URLs with query strings, fragments or secret path segments.
//
// Replay cannot filter the page address that it records at each full snapshot.
// isReplayExcludedPath() keeps Replay off on pages whose address holds a secret.
//
// Tests: node --test tests/

const FILTERED = '[Filtered]'

// Sentry masks only `title` and `placeholder` by default.
export const REPLAY_MASKED_ATTRIBUTES = ['title', 'placeholder', 'aria-label', 'alt']

export const REPLAY_BLOCKED_SELECTORS = [
  // Replay cannot mask `href` values. Only links to app paths with no query string
  // stay visible; all other links (other sites, mailto:, tel:, relative, ...) are blocked.
  'a[href]:not([href^="/"])',
  'a[href^="//"]',
  'a[href*="?"]',
  'a[href*="reset-password" i]',
  // The document viewers (components/AgGrid/OpenDocument.vue, OpenAeLetterDocument.vue)
  // show document HTML, with its own attributes.
  '#iframe',
]

// Pages where Replay must not start: the address holds a reset token and an email address.
const REPLAY_EXCLUDED_PATH = /^\/reset-password(\/|$)/i

// The reset-password token is a path segment, in the page URL and in the API URL.
const SECRET_PATH_SEGMENT = /(\/reset-password\/)[^/?#]+/gi

// Attribute values in a DOM breadcrumb message, e.g. `div > button[title="..."]`.
// A value ends at `"]` followed by the next attribute, the next element or the end.
// `type` values (button, submit, ...) are kept.
const MESSAGE_ATTRIBUTE_VALUE = /(\[(?!type=)[\w:-]+=")[\s\S]*?("\])(?=\[[\w:-]+="|\s>\s|$)/g

// Vue Router span data: `params.<name>` and `query.<name>` hold route values.
const ROUTE_VALUE_KEY = /^(params|query)\./

/** True if Replay must not start on a page with this path. */
export function isReplayExcludedPath(pathname) {
  return typeof pathname === 'string' && REPLAY_EXCLUDED_PATH.test(pathname)
}

/**
 * Remove the query string and fragment from a URL, and hide secret path segments.
 * Values that are not strings are returned unchanged.
 */
export function scrubUrl(value) {
  if (typeof value !== 'string') {
    return value
  }

  return value.replace(/[?#][\s\S]*$/, '').replace(SECRET_PATH_SEGMENT, `$1${FILTERED}`)
}

/**
 * Replace attribute values in an element description (`div > a[title="..."]`) with
 * [Filtered]. Sentry builds these for DOM breadcrumbs and Web Vitals tags.
 */
export function scrubElementDescription(value) {
  return typeof value === 'string' ? value.replace(MESSAGE_ATTRIBUTE_VALUE, `$1${FILTERED}$2`) : value
}

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function scrubUrlFields(data, keys) {
  if (!isPlainObject(data)) {
    return data
  }

  const scrubbed = { ...data }
  keys.filter((key) => key in scrubbed).forEach((key) => (scrubbed[key] = scrubUrl(scrubbed[key])))
  return scrubbed
}

function scrubBreadcrumb(breadcrumb) {
  // Console arguments can hold API errors with request headers and bodies.
  if (breadcrumb.category === 'console') {
    return null
  }

  const scrubbed = { ...breadcrumb }

  if (scrubbed.data) {
    scrubbed.data = scrubUrlFields(scrubbed.data, ['url', 'from', 'to', 'route'])
  }

  // DOM breadcrumbs (ui.click, ui.input, ...) describe the element from the live page,
  // so the masking in the recording does not apply to their message.
  if (typeof scrubbed.category === 'string' && scrubbed.category.startsWith('ui.') && typeof scrubbed.message === 'string') {
    scrubbed.message = scrubElementDescription(scrubbed.message)
  }

  return scrubbed
}

function scrubPerformanceSpan(span) {
  // For navigation and network spans, the description is the URL.
  const scrubbed = { ...span, description: scrubUrl(span.description) }

  if (scrubbed.data) {
    scrubbed.data = scrubUrlFields(scrubbed.data, ['previous', 'url'])
  }

  return scrubbed
}

/**
 * Replay `beforeAddRecordingEvent` callback. Sentry calls it for custom recording events
 * (breadcrumbs, performance spans and options). Returning null drops the event.
 * If this function throws, Sentry drops the event.
 */
export function scrubReplayRecordingEvent(event) {
  const tag = event?.data?.tag
  const payload = event?.data?.payload

  if (!isPlainObject(payload)) {
    return event
  }

  if (tag === 'breadcrumb') {
    const breadcrumb = scrubBreadcrumb(payload)
    return breadcrumb ? { ...event, data: { ...event.data, payload: breadcrumb } } : null
  }

  if (tag === 'performanceSpan') {
    return { ...event, data: { ...event.data, payload: scrubPerformanceSpan(payload) } }
  }

  return event
}

/**
 * Sentry event processor. For replay events only, it cleans the visited URLs, the page
 * URL and the Referer header, keeps only the user ID, and removes route values from the
 * trace data. Other events are returned unchanged.
 */
export function scrubReplayEvent(event) {
  if (event?.type !== 'replay_event') {
    return event
  }

  const scrubbed = { ...event }

  if (Array.isArray(scrubbed.urls)) {
    scrubbed.urls = scrubbed.urls.map(scrubUrl)
  }

  if (isPlainObject(scrubbed.request)) {
    scrubbed.request = scrubUrlFields(scrubbed.request, ['url'])

    if (scrubbed.request.headers) {
      scrubbed.request.headers = scrubUrlFields(scrubbed.request.headers, ['Referer'])
    }
  }

  if (isPlainObject(scrubbed.user)) {
    scrubbed.user = scrubbed.user.id === undefined ? {} : { id: scrubbed.user.id }
  }

  const traceData = scrubbed.contexts?.trace?.data
  if (isPlainObject(traceData)) {
    const data = Object.fromEntries(Object.entries(traceData).filter(([key]) => !ROUTE_VALUE_KEY.test(key)))
    scrubbed.contexts = { ...scrubbed.contexts, trace: { ...scrubbed.contexts.trace, data } }
  }

  return scrubbed
}
